import React, { useState, useRef } from 'react';
import SelectDropdown from './select';
import googleMapsService from '../services/GoogleMaps.service';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { toast } from 'react-toastify';

// Define the form data interface for the address step
export interface AddressFormData {
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode?: string;
  country: string;
  boardArea?: string;
}

// Define the errors interface
export interface AddressErrors {
  address1?: string;
  city?: string;
  postalCode?: string;
  state?: string;
  boardArea?: string;
}

// Define the props for the AddressStep component
export interface AddressStepProps {
  formData: AddressFormData;
  errors: AddressErrors;
  setFormData: React.Dispatch<React.SetStateAction<AddressFormData>>;
  setErrors: React.Dispatch<React.SetStateAction<AddressErrors>>;
  provinces: Array<{ id: number | string; name: string }>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  existingCoordinates?: [number, number]; // Add optional prop for existing coordinates
  onCoordinatesChange?: (coordinates: [number, number]) => void; // Add optional callback for coordinate changes
}

const AddressStep: React.FC<AddressStepProps> = ({
  formData,
  errors,
  setFormData,
  setErrors,
  provinces,
  handleInputChange,
  existingCoordinates,
  onCoordinatesChange
}) => {
  const [showAddressMap, setShowAddressMap] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const infoWindowRef = useRef<any>(null);
  const { isOnline } = useNetworkStatus();

  const provinceOptions = provinces.map(province =>
  ({
    value: province.id.toString(),
    label: province.name
  }))

  const handleDetectLocation = async () => {
    setShowAddressMap(true);
    setIsDetecting(true);
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
      });

      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;

      const google = await googleMapsService.loadMapsApi();
      const geocoder = new google.maps.Geocoder();
      const mapElement = document.getElementById('address-map');

      if (!mapElement) {
        throw new Error('Map container not found');
      }

      const addressMap = new google.maps.Map(mapElement, {
        center: { lat: latitude, lng: longitude },
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      });

      const updateAddressFromCoords = (lat: number, lng: number) => {
        geocoder.geocode(
          { location: { lat, lng } },
          (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              const address = results[0];
              const formattedAddress = address.formatted_address.split(',')[0];
              // Find city, state, and postal code from address components
              const addressComponents = address.address_components;
              const city = addressComponents.find((c: any) => c.types.includes('locality'))?.long_name || '';
              const postalCode = addressComponents.find((c: any) => c.types.includes('postal_code'))?.long_name || '';
              const province = addressComponents.find((c: any) => c.types.includes('administrative_area_level_1'))?.long_name || '';
              // Directly update the form data with the values
              handleInputChange({
                target: { name: 'address1', value: formattedAddress }
              } as React.ChangeEvent<HTMLInputElement>);

              if (city) {
                handleInputChange({
                  target: { name: 'city', value: city }
                } as React.ChangeEvent<HTMLInputElement>);
              }

              if (province) {
                const provinceId = provinceOptions.find((singleProvince: {value: string, label: string}) => singleProvince.label === province)?.value;
                if(provinceId){
                  handleInputChange({
                    target: { name: 'state', value: provinceId }
                  } as React.ChangeEvent<HTMLInputElement>);
                }
              }

              if (postalCode) {
                handleInputChange({
                  target: { name: 'postalCode', value: postalCode }
                } as React.ChangeEvent<HTMLInputElement>);
              }

              // Update coordinates
              if (onCoordinatesChange) {
                onCoordinatesChange([lat, lng]);
              }

              if (infoWindowRef.current) {
                infoWindowRef.current.setContent(`
                  <div style="padding: 10px; max-width: 250px;">
                    <h3 style="font-size: 16px; font-weight: medium; margin-bottom: 8px; color: black;">
                      Selected Location
                    </h3>
                    <p style="margin: 4px 0; color: #666;">
                      <strong>Address:</strong> ${address.formatted_address}
                    </p>
                  </div>
                `);
              }
            }
          }
        );
      };

      const marker = googleMapsService.addMarkerToMap(
        addressMap,
        { lat: latitude, lng: longitude },
        { draggable: true }
      );

      const infoWindow = new google.maps.InfoWindow({
        maxWidth: 250
      });
      infoWindowRef.current = infoWindow;

      marker.addListener('dragend', () => {
        const newPosition = marker.getPosition();
        if (!newPosition) return;
        updateAddressFromCoords(newPosition.lat(), newPosition.lng());
        infoWindow.open(addressMap, marker);
      });

      updateAddressFromCoords(latitude, longitude);
      infoWindow.open(addressMap, marker);
      setIsDetecting(false);

    } catch (error) {
      console.error('Error detecting location:', error);
      setShowAddressMap(false);
      infoWindowRef.current = null;
      toast.error('Failed to detect location. Please check your browser/device settings.');
    } finally {
      setIsDetecting(false);
    }
  };

  // Cleanup function when the map is closed
  const handleCloseMap = () => {
    setShowAddressMap(false);
    // Close any open info window
    if (infoWindowRef.current) {
      try {
        infoWindowRef.current.close();
      } catch (err) {
        console.error('Error closing info window on map close:', err);
      }
      infoWindowRef.current = null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">Address</h2>
        {
          isOnline && <button
            onClick={handleDetectLocation}
            className="text-sm text-[#A42823] hover:text-blue-800 flex items-center gap-1"
            id="detect-location-button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Select Location
          </button>
        }
      </div>

      {showAddressMap && (
        <div className="mb-6 relative">
          {isDetecting && (
            <div className="w-full h-[300px] rounded-lg shadow-md bg-gray-50 flex items-center justify-center">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full border-4 border-t-4 border-[#A52A2A] border-t-transparent animate-spin mb-2"></div>
                <p className="text-sm text-gray-600">Detecting location...</p>
              </div>
            </div>
          )}
          <div
            id="address-map"
            className="w-full h-[300px] rounded-lg shadow-md"
            style={{ minHeight: '300px', display: isDetecting ? 'none' : 'block' }}
          ></div>
          <button
            onClick={handleCloseMap}
            className="absolute top-2 right-2 bg-white p-2 rounded-full shadow-md hover:bg-gray-50"
            id="close-map-button"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 md:gap-y-6">
        {/* Address Line 1 Input */}
        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm text-gray-600 mb-1.5">
            Address Line 1 <span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="nope"
            type="text"
            name="address1"
            placeholder="Street address"
            className={`w-full px-3 py-2 border ${errors.address1 ? 'border-red-500' : 'border-gray-200'} rounded-md text-sm`}
            value={formData.address1}
            onChange={handleInputChange}
            id="address1-input"
          />
          {errors.address1 && <p className="mt-1 text-xs text-red-500">{errors.address1}</p>}
        </div>

        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm text-gray-600 mb-1.5">
            Address Line 2
          </label>
          <input
            autoComplete="nope"
            type="text"
            name="address2"
            placeholder="Apartment, suite, unit, etc."
            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
            value={formData.address2}
            onChange={handleInputChange}
            id="address2-input"
          />
        </div>

        {/* City Input */}
        <div className="col-span-1">
          <label className="block text-sm text-gray-600 mb-1.5">
            City/Town <span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="nope"
            type="text"
            name="city"
            placeholder="Enter city"
            className={`w-full px-3 py-2 border ${errors.city ? 'border-red-500' : 'border-gray-200'} rounded-md text-sm`}
            value={formData.city}
            onChange={handleInputChange}
            id="city-input"
          />
          {errors.city && <p className="mt-1 text-xs text-red-500">{errors.city}</p>}
        </div>

        {/* Province Dropdown */}
        <div className="col-span-1">
          <label className="block text-sm text-gray-600 mb-1.5">
            Province <span className="text-red-500">*</span>
          </label>
          <div className={`${errors.state ? 'border border-red-500 rounded-md' : ''}`} id="province-dropdown-container">
            <SelectDropdown
              options={provinceOptions}
              onSelect={(value) => {
                setFormData({ ...formData, state: value });
                if (errors.state) {
                  setErrors(prev => ({ ...prev, state: '' }));
                }
              }}
              getValue={(option) => option.value}
              getDisplayText={(option) => option.label}
              placeholder="Select province"
              selectedValue={formData.state}
              testId="province-select"
            />
          </div>
          {errors.state && <p className="mt-1 text-xs text-red-500">{errors.state}</p>}
        </div>

        {/* Postal Code Input */}
        <div className="col-span-1">
          <label className="block text-sm text-gray-600 mb-1.5">
            Postal Code
          </label>
          <input
            autoComplete="nope"
            type="text"
            name="postalCode"
            placeholder="Enter postal code"
            className={`w-full px-3 py-2 border ${errors.postalCode ? 'border-red-500' : 'border-gray-200'} rounded-md text-sm`}
            value={formData.postalCode}
            onChange={handleInputChange}
            id="postal-code-input"
          />
          {errors.postalCode && <p className="mt-1 text-xs text-red-500">{errors.postalCode}</p>}
        </div>
      </div>
    </div>
  );
};

// Maps the first letter of a Canadian postal code to the province(s) it belongs to
export const POSTAL_CODE_PROVINCE_MAP: Record<string, string[]> = {
  A: ['Newfoundland and Labrador'],
  B: ['Nova Scotia'],
  C: ['Prince Edward Island'],
  E: ['New Brunswick'],
  G: ['Québec'],
  H: ['Québec'],
  J: ['Québec'],
  K: ['Ontario'],
  L: ['Ontario'],
  M: ['Ontario'],
  N: ['Ontario'],
  P: ['Ontario'],
  R: ['Manitoba'],
  S: ['Saskatchewan'],
  T: ['Alberta'],
  V: ['British Columbia'],
  X: ['Northwest Territories', 'Nunavut'],
  Y: ['Yukon'],
};

// Add a validation function for the address step
export const validateAddressStep = (formData: AddressFormData): { isValid: boolean; errors: AddressErrors } => {
  const newErrors: AddressErrors = {};
  let hasErrors = false;

  // Required fields for address
  const addressRequiredFields: Array<keyof AddressFormData> = ['address1', 'city', 'state'];

  const getFieldDisplayName = (field: string): string => {
    switch (field) {
      case 'address1':
        return 'Address';
      case 'state':
        return 'Province';
      // case 'postalCode':
      //   return 'Postal Code';
      default:
        return field.charAt(0).toUpperCase() + field.slice(1);
    }
  };

  // Validate each required field
  addressRequiredFields.forEach(field => {
    const value = formData[field];
    if (value === undefined || value.toString().trim() === '') {
      // Map field names to error field names (now they match)
      let errorField: keyof AddressErrors;
      switch (field) {
        case 'address1':
          errorField = 'address1';
          break;
        case 'city':
          errorField = 'city';
          break;
        case 'postalCode':
          errorField = 'postalCode';
          break;
        default:
          errorField = field as keyof AddressErrors;
      }
      newErrors[errorField] = `${getFieldDisplayName(field)} is required`;
      hasErrors = true;
    }
  });

  // Validate postal code format (Canadian postal code)
  if (formData.postalCode && !/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(formData.postalCode.trim())) {
    newErrors.postalCode = 'Please enter a valid postal code (e.g., A1A 1A1)';
    hasErrors = true;
  }

  return { isValid: !hasErrors, errors: newErrors };
};

export default AddressStep; 